



















































































import {
  faCamera,
  faCheck,
  faExclamationTriangle,
  faFile,
  faImage,
  faPencilAlt,
  faPlus,
  faTimes,
  faTrash
} from '@fortawesome/free-solid-svg-icons'
import {
  defineComponent,
  ref,
  PropType,
  toRefs,
  watch,
  computed,
  nextTick
} from '@nuxtjs/composition-api'

import { useDraggable, useDroppable } from '~/compositions/cursor'
import { useFileUpload } from '~/compositions/files/upload'
import { useI18n } from '~/compositions/i18n'
import { UploaderFile } from '~/models/photos/uploader'
import CDynamicIcon from '~/components/shared/configurable/icon/CDynamicIcon.vue'
import { Icon } from '~/models/icons/types'
import CFileUploaderEntry from '~/components/shared/configurable/file/CFileUploaderEntry.vue'
import CDraggable from '~/components/shared/configurable/CDraggable.vue'
import CFloatingButton from '~/components/shared/configurable/CFloatingButton.vue'
import { isElementInViewport } from '~/utils/dom'

export default defineComponent({
  components: { CFloatingButton, CDraggable, CDynamicIcon, CFileUploaderEntry },
  props: {
    acceptedFileTypes: {
      type: Array as PropType<string[]>,
      default() {
        return []
      }
    },
    max: {
      type: Number,
      default: 60
    },
    loading: {
      type: Boolean,
      default: false
    },
    overlayOpacity: {
      type: Number,
      default: 1
    },
    files: {
      type: Array as PropType<UploaderFile[]>,
      default: () => []
    },
    placeholderIcon: {
      type: Object as PropType<Icon>,
      default() {
        return faImage
      }
    },
    mobileButtonText: {
      type: String,
      default() {
        return this.$t('add files').toString()
      }
    },
    placeholderText: {
      type: String,
      default() {
        return this.$t('drop your files or click here').toString()
      }
    },
    filesHaveTitles: {
      type: Boolean,
      default: false
    },
    titleInputPlaceholder: {
      type: String,
      default: null
    },
    showFloatingButton: {
      type: Boolean,
      required: false,
      default: false
    },
    deleteFileMsg: {
      type: String,
      default() {
        return this.$t('do you really want to delete this photo?')
      }
    }
  },
  setup(props, { emit }) {
    const { acceptedFileTypes, max, loading, files, deleteFileMsg } = toRefs(
      props
    )
    const dropInputTemplateRef = ref<HTMLElement | null>(null)
    const internalFiles = ref(files.value)
    const internalLoading = ref(loading.value)
    const draggableRef = ref(null)

    const isVisible = ref(false)
    const { dragging, onDragStart, onDragEnd } = useDraggable()
    const { droppingOver, onDrop, onDragOver, onDragLeave } = useDroppable(
      dragging,
      fileDropped
    )
    const { filesAreOfAcceptableType } = useFileUpload()
    const { tc, t } = useI18n()

    function addFiles(fileList: FileList) {
      if (!fileList || !fileList.length) {
        return
      }
      // Uploaded files are above max length
      if (fileList.length + internalFiles.value.length > max.value) {
        return alert(
          tc(
            'too many attachments, maximum is 1 file | too many attachments, maximum is {count} files',
            max.value
          )
        )
      }
      if (
        acceptedFileTypes.value?.length &&
        !filesAreOfAcceptableType(fileList, acceptedFileTypes.value)
      ) {
        return alert(t('this file type is not supported'))
      }

      emit('add', Array.from(fileList))
    }
    function fileInputChanged(e: Event) {
      addFiles((e.target as any).files)
    }
    function fileDropped(e: DragEvent) {
      addFiles((e.dataTransfer as any).files)
    }
    function orderChanged() {
      emit('change', internalFiles.value)
    }
    function handleDeleteFile({
      name,
      id,
      file
    }: {
      name: string
      id: string
      file: string
    }) {
      if (!confirm(deleteFileMsg.value)) {
        return
      }
      emit('delete', { name, id, file })
    }
    function handleFileTitleChange(name: string, title: string) {
      emit('title-change', { name, title })
    }
    function triggerInput(): void {
      if (dropInputTemplateRef.value) {
        dropInputTemplateRef.value.value = null
        dropInputTemplateRef.value.click()
      }
    }

    watch(loading, newLoading => (internalLoading.value = newLoading))
    watch(files, newPhotos => (internalFiles.value = newPhotos))

    const icons = {
      file: faFile,
      plus: faPlus,
      pencilAlt: faPencilAlt,
      exclamationTriangle: faExclamationTriangle,
      trash: faTrash,
      checkMark: faCheck,
      xMark: faTimes
    }

    const placeholderIconClasses = computed(() => {
      const textColor = () =>
        droppingOver.value ? ['tw-text-white'] : ['tw-text-grey-400']
      const fontSize = () =>
        hasImages.value ? ['tw-text-4xl'] : ['tw-text-5xl']
      return [...textColor(), ...fontSize()]
    })
    const placeholderTextClasses = computed(() => {
      const textColor = () =>
        droppingOver.value ? ['tw-text-white'] : ['tw-text-grey-600']
      return [...textColor()]
    })
    const dropContainerClasses = computed(() => {
      const backgroundColorClasses = () => {
        if (droppingOver.value) {
          return ['tw-bg-blue']
        } else if (dragging.value) {
          return ['tw-bg-grey-200']
        }
        return ['tw-bg-grey-100']
      }

      const hasImagesClasses = () =>
        hasImages.value ? ['tw-px-10', 'tw-py-8'] : ['tw-px-12', 'tw-py-12']

      return [...backgroundColorClasses(), ...hasImagesClasses()]
    })
    const hasImages = computed(() => internalFiles.value.length > 0)

    const visibilityChanged = (visible: boolean) => {
      isVisible.value = visible
    }

    const scrollNotVisibleFileAtIndex = async (index: number) => {
      if (!process.client) {
        return
      }
      await nextTick()
      if (
        draggableRef.value?.$el?.childNodes[index] &&
        isElementInViewport(draggableRef.value.$el.childNodes[index]) === false
      ) {
        draggableRef.value.$el.childNodes[index].scrollIntoView({
          behavior: 'smooth'
        })
      }
    }
    return {
      dragging,
      droppingOver,
      onDragStart,
      onDragEnd,
      onDrop,
      onDragOver,
      onDragLeave,
      internalLoading,
      icons,
      fileInputChanged,
      orderChanged,
      handleDeleteFile,
      triggerInput,
      dropInputTemplateRef,
      internalFiles,
      placeholderTextClasses,
      placeholderIconClasses,
      dropContainerClasses,
      hasImages,
      handleFileTitleChange,
      faCamera,
      visibilityChanged,
      isVisible,
      draggableRef,
      scrollNotVisibleFileAtIndex
    }
  }
})
